// The renderer.
//
// One canvas, one draw call per frame, zero DOM per entity.
//
// The system this replaces built a `<div>` for every projectile, each
// with two blurred box-shadows, and could hold 150-200 of them alive at
// once. Every frame the browser was asked to lay out, composite and
// blur a couple of hundred boxes — on a laptop that is a slideshow, and
// on a phone it is a heater. None of that work was ever necessary:
// bullets are dots, and dots are what a 2D context is for.
//
// Three rules keep it fast, and they are worth stating because breaking
// any one of them silently undoes the rewrite:
//
//   1. **No allocation in the hot path.** Interpolated bullet positions
//      go into a growable `Float32Array`, dynamic opacity goes through
//      `globalAlpha` rather than freshly-built `rgba()` strings, and
//      gradients are built once and re-used by translating the context
//      onto them.
//   2. **No `shadowBlur`, ever.** It is the single most expensive
//      property in the 2D API. Glow comes from a pre-rendered sprite
//      per bullet kind, drawn with `drawImage`.
//   3. **Batch by kind.** One pass per sprite, so the source image
//      never changes mid-run.
//
// Coordinates: everything below draws in **logical arena units**. The
// context transform maps logical -> device pixels once per frame, so a
// radius of `PLAYER_R` on screen is exactly the radius the sim tests.
// That is a fairness property, not a convenience — the old system used
// CSS pixels for hit radii, so resizing the window changed the game.
//
// Angles are **turns**, as everywhere else in this folder (0 = right,
// 0.25 = down). `ctx.arc` wants radians, so turns get multiplied by TAU
// at the call site and nowhere else.

import { clamp, cosT, sinT } from "./rng";
import {
  ARENA_H,
  ARENA_W,
  GRAZE_R,
  PLAYER_R,
  type BulletKind,
  type SimState,
} from "./types";

const TAU = Math.PI * 2;

// ── The logical -> screen transform ───────────────────────────────────

/** Device pixel ratio is capped here. Beyond 2 the extra pixels are
 *  invisible and the fill cost is quadratic — a 3x phone would be
 *  shading 2.25x the pixels of a 2x one for no perceptible gain. */
export const MAX_DPR = 2;

/** Below this the arena would be too small to play in; the shrink
 *  device is meant to squeeze, not to close. */
export const MIN_ARENA_SCALE = 0.2;

export type ViewTransform = {
  /** Capped device pixel ratio actually in use. */
  dpr: number;
  /** Canvas size in CSS pixels. */
  cssW: number;
  cssH: number;
  /** Logical units -> CSS pixels, including `arenaScale`. */
  scale: number;
  /** CSS-pixel position of logical (0, 0). */
  ox: number;
  oy: number;
  /** The clamped arena scale folded into `scale`. */
  arenaScale: number;
};

export type Pt = { x: number; y: number };

/**
 * Fit the square arena into a canvas of any shape.
 *
 * Letterboxed, never stretched: a non-uniform scale would change the
 * shape of every pattern, so a ring would land as an ellipse and the
 * gap in a wall would move. The arena is centred in whatever space is
 * left over, and `arenaScale` shrinks it about that same centre — which
 * is what makes the runlevel-2 "swap thrash" device read as the walls
 * closing in rather than as the camera zooming.
 *
 * Pass `out` to write in place; the render loop does, so that a frame
 * costs no allocation at all.
 */
export function computeTransform(
  cssW: number,
  cssH: number,
  dpr: number,
  arenaScale = 1,
  out?: ViewTransform,
): ViewTransform {
  const w = Number.isFinite(cssW) && cssW > 0 ? cssW : 0;
  const h = Number.isFinite(cssH) && cssH > 0 ? cssH : 0;
  const d = clamp(Number.isFinite(dpr) && dpr > 0 ? dpr : 1, 1, MAX_DPR);
  const as = clamp(Number.isFinite(arenaScale) ? arenaScale : 1, MIN_ARENA_SCALE, 1);

  const fit = Math.min(w / ARENA_W, h / ARENA_H);
  const scale = fit * as;
  const ox = (w - ARENA_W * scale) / 2;
  const oy = (h - ARENA_H * scale) / 2;

  const t = out ?? ({} as ViewTransform);
  t.dpr = d;
  t.cssW = w;
  t.cssH = h;
  t.scale = scale;
  t.ox = ox;
  t.oy = oy;
  t.arenaScale = as;
  return t;
}

/** Logical arena units -> CSS pixels. Writes into `out`. */
export function toScreen(t: ViewTransform, x: number, y: number, out: Pt): Pt {
  out.x = t.ox + x * t.scale;
  out.y = t.oy + y * t.scale;
  return out;
}

/**
 * CSS pixels -> logical arena units. This is how the input layer turns a
 * pointer position into `SimInput`, so it must be the exact inverse of
 * `toScreen` — including under `arenaScale`, or the cursor would drift
 * away from the hitbox exactly when the arena is at its meanest.
 */
export function toLogical(t: ViewTransform, sx: number, sy: number, out: Pt): Pt {
  if (t.scale <= 0) {
    out.x = ARENA_W / 2;
    out.y = ARENA_H / 2;
    return out;
  }
  out.x = (sx - t.ox) / t.scale;
  out.y = (sy - t.oy) / t.scale;
  return out;
}

// ── Palette ───────────────────────────────────────────────────────────
//
// midnight skenOS: teal / pink / amber / lime on near-black. Violet is
// the one addition — `child` bullets need to be separable from all four
// at a glance, and there are six bullet kinds to four house colours.

export const DAEMON_PALETTE = {
  letterbox: "#02030a",
  floor: "#05070f",
  grid: "#5eead4",
  wall: "#5eead4",
  wallShrunk: "#ff5fa2",
  player: "#ffffff",
  playerRim: "#5eead4",
  graze: "#5eead4",
  priv: "#ffd66e",
  iframe: "#ffd66e",
  daemon: "#ff5fa2",
  guard: "#ffd66e",
  child: "#c8a2ff",
  zombie: "#6f8a86",
  reap: "#b4ff6e",
  telegraphSpawn: "#ffd66e",
  telegraphLaser: "#ff5fa2",
  telegraphSlam: "#b4ff6e",
} as const;

/**
 * Bullet colour by kind. Patterns are only learnable if their pieces are
 * separable, so each family owns one hue and keeps it.
 *
 * `heal` is the deliberate outlier. In the old game it was a green dot
 * in a palette that already spent green on heal *sparkles*, which made
 * the one bullet you are supposed to run toward indistinguishable from
 * ambient decoration — nobody found it. Here it is white, hollow, and
 * carries a cross: the only bullet whose *shape* differs, so it reads
 * even for a player who cannot separate the hues at all.
 */
export const BULLET_COLOURS: Record<BulletKind, string> = {
  plain: "#5eead4",
  heavy: "#ff5fa2",
  homing: "#ffd66e",
  laser: "#b4ff6e",
  child: "#c8a2ff",
  heal: "#ffffff",
};

/** Painter's order. `heal` goes last so it is never buried, `heavy`
 *  second-last because it is the one you must not misjudge. */
const KIND_ORDER: readonly BulletKind[] = ["plain", "laser", "homing", "child", "heavy", "heal"];

const KIND_INDEX: Record<BulletKind, number> = {
  plain: 0,
  laser: 1,
  homing: 2,
  child: 3,
  heavy: 4,
  heal: 5,
};

// ── Sprites ───────────────────────────────────────────────────────────

/** Source resolution of a bullet sprite. Generous enough that a bullet
 *  drawn at 40 device pixels still downsamples rather than stretches. */
const SPRITE_PX = 128;

/** Sprite extent as a multiple of the bullet's true radius. The solid
 *  disc ends at exactly `r`; the rest is glow. Nothing outside `r` is
 *  dangerous, and it is drawn dim enough to say so. */
const GLOW_PAD = 1.8;

type SpriteSpec = {
  /** Hot centre. */
  core: string;
  /** The body, out to exactly the true radius. */
  body: string;
  /** Glow just outside the body. */
  halo: string;
  /** Same hue, fully transparent — the outer stop. */
  fade: string;
  /** Contrast ring drawn on the true radius. */
  rim: string;
};

const SPRITE_SPECS: Record<BulletKind, SpriteSpec> = {
  plain: {
    core: "#f2fffc",
    body: "#5eead4",
    halo: "rgba(94,234,212,0.42)",
    fade: "rgba(94,234,212,0)",
    rim: "rgba(3,18,16,0.55)",
  },
  heavy: {
    core: "#ffdbeb",
    body: "#ff5fa2",
    halo: "rgba(255,95,162,0.46)",
    fade: "rgba(255,95,162,0)",
    rim: "rgba(26,3,13,0.72)",
  },
  homing: {
    core: "#fff8e2",
    body: "#ffd66e",
    halo: "rgba(255,214,110,0.42)",
    fade: "rgba(255,214,110,0)",
    rim: "rgba(26,19,3,0.62)",
  },
  laser: {
    core: "#f5ffe8",
    body: "#b4ff6e",
    halo: "rgba(180,255,110,0.42)",
    fade: "rgba(180,255,110,0)",
    rim: "rgba(14,24,5,0.62)",
  },
  child: {
    core: "#f1e9ff",
    body: "#c8a2ff",
    halo: "rgba(200,162,255,0.42)",
    fade: "rgba(200,162,255,0)",
    rim: "rgba(16,8,28,0.62)",
  },
  heal: {
    core: "#ffffff",
    body: "#ffffff",
    halo: "rgba(255,255,255,0.34)",
    fade: "rgba(255,255,255,0)",
    rim: "rgba(255,255,255,0.9)",
  },
};

function get2d(cv: HTMLCanvasElement): CanvasRenderingContext2D | null {
  // jsdom has no canvas backend and returns null; some locked-down
  // browsers throw outright. Either way the renderer must degrade to a
  // no-op rather than take the app down with it.
  try {
    return cv.getContext("2d", { alpha: false });
  } catch {
    return null;
  }
}

function buildSprite(kind: BulletKind): HTMLCanvasElement | null {
  if (typeof document === "undefined") return null;
  const cv = document.createElement("canvas");
  cv.width = SPRITE_PX;
  cv.height = SPRITE_PX;
  let c: CanvasRenderingContext2D | null = null;
  try {
    c = cv.getContext("2d");
  } catch {
    c = null;
  }
  if (!c) return null;

  const spec = SPRITE_SPECS[kind];
  const m = SPRITE_PX / 2;
  const core = m / GLOW_PAD; // the true radius, in sprite pixels
  const edge = core / m; // ...as a gradient stop

  if (kind === "heal") {
    // Hollow ring plus a cross. The silhouette does the work: this is
    // the only bullet you are meant to touch, so it must not look like
    // any of the ones that will kill you for touching them.
    const g = c.createRadialGradient(m, m, core * 0.9, m, m, m);
    g.addColorStop(0, spec.halo);
    g.addColorStop(1, spec.fade);
    c.fillStyle = g;
    c.beginPath();
    c.arc(m, m, m, 0, TAU);
    c.fill();

    c.strokeStyle = spec.rim;
    c.lineWidth = core * 0.34;
    c.beginPath();
    c.arc(m, m, core * 0.82, 0, TAU);
    c.stroke();

    c.lineCap = "round";
    c.lineWidth = core * 0.26;
    c.strokeStyle = spec.core;
    c.beginPath();
    c.moveTo(m - core * 0.4, m);
    c.lineTo(m + core * 0.4, m);
    c.moveTo(m, m - core * 0.4);
    c.lineTo(m, m + core * 0.4);
    c.stroke();
    return cv;
  }

  const g = c.createRadialGradient(m, m, 0, m, m, m);
  g.addColorStop(0, spec.core);
  g.addColorStop(edge * 0.5, spec.body);
  g.addColorStop(edge, spec.body);
  g.addColorStop(Math.min(0.999, edge + 0.02), spec.halo);
  g.addColorStop(1, spec.fade);
  c.fillStyle = g;
  c.beginPath();
  c.arc(m, m, m, 0, TAU);
  c.fill();

  // A dark rim on the true radius. Without it a bright bullet crossing
  // a bright telegraph loses its edge, and the edge is the thing being
  // judged.
  c.strokeStyle = spec.rim;
  c.lineWidth = Math.max(2, core * 0.12);
  c.beginPath();
  c.arc(m, m, core - c.lineWidth / 2, 0, TAU);
  c.stroke();

  if (kind === "heavy") {
    // A hollow centre reads as mass. Heavy bullets are the ones worth
    // respecting and they should look it from across the arena.
    c.globalCompositeOperation = "destination-out";
    c.beginPath();
    c.arc(m, m, core * 0.3, 0, TAU);
    c.fill();
    c.globalCompositeOperation = "source-over";
  }

  return cv;
}

// ── Geometry constants ────────────────────────────────────────────────

const ARENA_RADIUS = 18;
const DAEMON_R = 54;
const CHILD_R = 30;
const ZOMBIE_R = 26;
const CURSOR_R = PLAYER_R * 2.8;

/**
 * Reference telegraph length, in ticks, used only to turn the remaining
 * `ticks` into a 0-1 urgency for drawing. The real lead is
 * `TELEGRAPH_TICKS` scaled by difficulty (roughly 19-35 ticks), and a
 * `Telegraph` does not carry its own total — so this is a display
 * constant, not a fairness one. Erring long simply means a telegraph
 * starts its life looking slightly less urgent than it is.
 */
const TELEGRAPH_REF = 36;

/**
 * Reference reap dwell, in ticks. `Zombie.reap` counts down and carries
 * no maximum, so the progress arc is drawn against this.
 */
const REAP_REF = 90;

// Pre-allocated dash patterns. Passing an array literal to setLineDash
// every frame would allocate once per entity per frame.
const DASH_NONE: number[] = [];
const DASH_ZOMBIE: number[] = [10, 8];
const DASH_GRAZE: number[] = [7, 9];
const DASH_GUARD: number[] = [14, 10];

// ── The renderer ──────────────────────────────────────────────────────

export type Renderer = {
  /** Tell the renderer how big its canvas is, in CSS pixels. */
  resize(cssW: number, cssH: number): void;
  /**
   * Draw one frame.
   *
   * `alpha` is the fraction of a tick elapsed since `state` was
   * produced: the sim runs at a fixed 60Hz and the display may be at
   * 120 or 144, so positions are interpolated from the previous tick to
   * this one. The renderer keeps the previous state itself — callers
   * hand over the current one and nothing else.
   *
   * `now` is a wall-clock millisecond stamp, used only for decoration
   * (pulses, spin, dash offsets). Nothing that matters to play is
   * derived from it; that all comes from `state`.
   */
  draw(state: SimState, alpha: number, now: number): void;
  destroy(): void;
};

/** Upper bound on either canvas dimension in CSS pixels. Comfortably
 *  larger than any real display, small enough that a runaway layout
 *  cannot exhaust memory. */
const MAX_CSS_SIDE = 8192;

export function createRenderer(canvas: HTMLCanvasElement): Renderer {
  const ctx = get2d(canvas);

  let cssW = canvas.clientWidth || ARENA_W;
  let cssH = canvas.clientHeight || ARENA_H;
  let dpr = readDpr();
  const view = computeTransform(cssW, cssH, dpr, 1);

  let sprites: (HTMLCanvasElement | null)[] | null = null;
  let vignette: CanvasGradient | null = null;
  let daemonHalo: CanvasGradient | null = null;
  let playerGlow: CanvasGradient | null = null;

  // The two most recent states, for interpolation. `draw` is only ever
  // handed the current one, so the previous is kept here.
  let lastState: SimState | null = null;
  let prevState: SimState | null = null;

  // Interpolated bullets: stride 4 of (x, y, r, kindIndex). Grown by
  // doubling, so steady state allocates nothing.
  let buf: Float32Array = new Float32Array(256 * 4);

  // Reduced motion cuts *decoration* only. It must never cut telegraph
  // legibility — the old CSS shortened the telegraph animation under
  // `prefers-reduced-motion`, which made the fairness cue harder to
  // read for precisely the people who asked for less motion. Here the
  // reduced path holds every cue at its brightest and stops it moving.
  const mql =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
  let reduced = mql?.matches ?? false;
  const onMotionChange = (e: MediaQueryListEvent): void => {
    reduced = e.matches;
  };
  if (mql && typeof mql.addEventListener === "function") {
    mql.addEventListener("change", onMotionChange);
  }

  const resize = (w: number, h: number): void => {
    // Clamped, and not merely for tidiness. If the stylesheet fails to
    // load, the canvas and its parent size from each other with nothing
    // to stop them — observed in production at 16,777,216 CSS pixels
    // square, which at dpr 2 is a 33-megapixel-per-side backing store
    // and several gigabytes of allocation. A renderer should degrade to
    // a wrong-looking frame, never to a dead tab.
    cssW = Number.isFinite(w) && w > 0 ? Math.min(w, MAX_CSS_SIDE) : 1;
    cssH = Number.isFinite(h) && h > 0 ? Math.min(h, MAX_CSS_SIDE) : 1;
    dpr = readDpr();
    const pw = Math.max(1, Math.round(cssW * dpr));
    const ph = Math.max(1, Math.round(cssH * dpr));
    if (canvas.width !== pw) canvas.width = pw;
    if (canvas.height !== ph) canvas.height = ph;
    computeTransform(cssW, cssH, dpr, view.arenaScale, view);
  };

  const draw = (state: SimState, alpha: number, now: number): void => {
    // Track ticks even without a context, so that a renderer which
    // starts headless and later gets one is not a frame behind.
    if (lastState === null) {
      lastState = state;
      prevState = state;
    } else if (state !== lastState) {
      // Only interpolate across a genuine one-tick advance. A restart,
      // a rewind or a replay scrub would otherwise smear the player
      // across the arena.
      prevState = state.tick === lastState.tick + 1 ? lastState : state;
      lastState = state;
    }

    if (!ctx) return;
    if (!sprites) sprites = KIND_ORDER.map(buildSprite);

    // Dragging a window between a retina and a non-retina display fires
    // no resize event, so the backing store would quietly stay at the
    // wrong resolution and everything would go soft. One property read
    // a frame is cheaper than the bug report.
    if (readDpr() !== dpr) resize(cssW, cssH);

    const prev = prevState ?? state;
    const a = clamp(alpha, 0, 1);

    computeTransform(cssW, cssH, dpr, state.arenaScale, view);
    const k = view.scale * view.dpr; // logical units -> device pixels

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.fillStyle = DAEMON_PALETTE.letterbox;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (k <= 0) return;

    ctx.setTransform(k, 0, 0, k, view.ox * view.dpr, view.oy * view.dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.setLineDash(DASH_NONE);

    // One device pixel, expressed in logical units. Every stroke width
    // below is a logical value floored at a device pixel or two, so
    // nothing vanishes in a small window and nothing turns into a slab
    // in a large one.
    const px = 1 / k;

    if (!vignette) {
      vignette = ctx.createRadialGradient(
        ARENA_W / 2,
        ARENA_H / 2,
        ARENA_W * 0.3,
        ARENA_W / 2,
        ARENA_H / 2,
        ARENA_W * 0.74,
      );
      vignette.addColorStop(0, "rgba(0,0,0,0)");
      vignette.addColorStop(1, "rgba(0,0,0,0.5)");
    }
    if (!daemonHalo) {
      daemonHalo = ctx.createRadialGradient(0, 0, DAEMON_R * 0.55, 0, 0, DAEMON_R * 2.4);
      daemonHalo.addColorStop(0, "rgba(255,95,162,0.32)");
      daemonHalo.addColorStop(0.5, "rgba(255,95,162,0.11)");
      daemonHalo.addColorStop(1, "rgba(255,95,162,0)");
    }
    if (!playerGlow) {
      playerGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, PLAYER_R * 4);
      playerGlow.addColorStop(0, "rgba(255,255,255,0.5)");
      playerGlow.addColorStop(0.35, "rgba(94,234,212,0.22)");
      playerGlow.addColorStop(1, "rgba(94,234,212,0)");
    }

    drawArena(ctx, state, px, vignette);

    ctx.save();
    arenaPath(ctx);
    ctx.clip();

    drawTelegraphs(ctx, state, px, now, reduced);
    drawZombies(ctx, state, px, now, reduced);
    drawChildren(ctx, state, px, now, reduced);
    drawDaemon(ctx, state, prev, a, px, now, reduced, daemonHalo);
    buf = drawBullets(ctx, state, prev, a, sprites, buf);
    drawPlayer(ctx, state, prev, a, px, now, reduced, playerGlow);

    ctx.restore();
    ctx.globalAlpha = 1;
    ctx.setLineDash(DASH_NONE);
  };

  const destroy = (): void => {
    if (mql && typeof mql.removeEventListener === "function") {
      mql.removeEventListener("change", onMotionChange);
    }
    sprites = null;
    vignette = null;
    daemonHalo = null;
    playerGlow = null;
    lastState = null;
    prevState = null;
    buf = new Float32Array(0);
  };

  // Size the backing store from whatever the element already measures,
  // so a host that forgets to call resize still gets a correct first
  // frame rather than the 300x150 canvas default.
  resize(cssW, cssH);

  return { resize, draw, destroy };
}

function readDpr(): number {
  const d = typeof window !== "undefined" ? window.devicePixelRatio : 1;
  return clamp(Number.isFinite(d) && d > 0 ? d : 1, 1, MAX_DPR);
}

function arenaPath(c: CanvasRenderingContext2D): void {
  c.beginPath();
  if (typeof c.roundRect === "function") c.roundRect(0, 0, ARENA_W, ARENA_H, ARENA_RADIUS);
  else c.rect(0, 0, ARENA_W, ARENA_H);
}

// ── Layers ────────────────────────────────────────────────────────────

function drawArena(
  c: CanvasRenderingContext2D,
  state: SimState,
  px: number,
  vignette: CanvasGradient,
): void {
  arenaPath(c);
  c.fillStyle = DAEMON_PALETTE.floor;
  c.fill();

  c.save();
  arenaPath(c);
  c.clip();

  // A tenth-of-arena grid. Faint on purpose: it exists so that movement
  // has a frame of reference and so a shrinking arena is legible, not
  // to be looked at.
  c.strokeStyle = DAEMON_PALETTE.grid;
  c.globalAlpha = 0.055;
  c.lineWidth = Math.max(1, px);
  c.beginPath();
  for (let i = 1; i < 10; i++) {
    const p = (i / 10) * ARENA_W;
    c.moveTo(p, 0);
    c.lineTo(p, ARENA_H);
    c.moveTo(0, p);
    c.lineTo(ARENA_W, p);
  }
  c.stroke();

  c.globalAlpha = 1;
  c.fillStyle = vignette;
  c.fillRect(0, 0, ARENA_W, ARENA_H);
  c.restore();

  // Walls. When the arena is shrunk they go pink and thicken — the
  // playfield closing in is the whole point of the runlevel-2 device
  // and it should be impossible to miss.
  const shrunk = state.arenaScale < 0.999;
  c.strokeStyle = shrunk ? DAEMON_PALETTE.wallShrunk : DAEMON_PALETTE.wall;
  c.globalAlpha = shrunk ? 0.95 : 0.5;
  c.lineWidth = Math.max(shrunk ? 7 : 3.5, 2 * px);
  arenaPath(c);
  c.stroke();

  if (shrunk) {
    c.globalAlpha = 0.18;
    c.lineWidth = Math.max(22, 6 * px);
    arenaPath(c);
    c.stroke();
  }

  // Viewfinder corners. Cheap, and they make the arena read as a
  // bounded instrument rather than as a window edge.
  const t = 58;
  c.globalAlpha = shrunk ? 0.9 : 0.7;
  c.lineWidth = Math.max(5, 2 * px);
  c.beginPath();
  c.moveTo(0, t);
  c.lineTo(0, 0);
  c.lineTo(t, 0);
  c.moveTo(ARENA_W - t, 0);
  c.lineTo(ARENA_W, 0);
  c.lineTo(ARENA_W, t);
  c.moveTo(ARENA_W, ARENA_H - t);
  c.lineTo(ARENA_W, ARENA_H);
  c.lineTo(ARENA_W - t, ARENA_H);
  c.moveTo(t, ARENA_H);
  c.lineTo(0, ARENA_H);
  c.lineTo(0, ARENA_H - t);
  c.stroke();

  c.globalAlpha = 1;
}

function telegraphColour(kind: "spawn" | "laser" | "slam"): string {
  if (kind === "laser") return DAEMON_PALETTE.telegraphLaser;
  if (kind === "slam") return DAEMON_PALETTE.telegraphSlam;
  return DAEMON_PALETTE.telegraphSpawn;
}

/**
 * Telegraphs — the fairness cue, and therefore the loudest thing on
 * screen after the player's own dot.
 *
 * Three signals, deliberately redundant so that no single channel is
 * load-bearing: a filled disc that says *where*, a bold ring that says
 * *how big*, and a ring closing toward the centre that says *when*.
 * The closing ring is geometry, not animation — it is a position, so it
 * can be read from a single glance at a single frame. Only the opacity
 * pulse is animation, and that is the one thing reduced motion drops,
 * by pinning it to full brightness rather than by shortening it.
 */
function drawTelegraphs(
  c: CanvasRenderingContext2D,
  state: SimState,
  px: number,
  now: number,
  reduced: boolean,
): void {
  const pulse = reduced ? 1 : 0.74 + 0.26 * (0.5 + 0.5 * sinT(now * 0.0012));

  for (let i = 0; i < state.telegraphs.length; i++) {
    const t = state.telegraphs[i]!;
    const colour = telegraphColour(t.kind);
    const left = clamp(t.ticks / TELEGRAPH_REF, 0, 1);
    const urgency = 1 - left;

    c.fillStyle = colour;
    c.strokeStyle = colour;

    c.globalAlpha = (0.09 + 0.17 * urgency) * pulse;
    c.beginPath();
    c.arc(t.x, t.y, t.r, 0, TAU);
    c.fill();

    c.globalAlpha = (0.6 + 0.4 * urgency) * pulse;
    c.lineWidth = Math.max(5 + 4 * urgency, 2.5 * px);
    c.beginPath();
    c.arc(t.x, t.y, t.r, 0, TAU);
    c.stroke();

    // The timer, drawn as a radius.
    c.globalAlpha = 0.9;
    c.lineWidth = Math.max(3.5, 1.5 * px);
    c.beginPath();
    c.arc(t.x, t.y, Math.max(1, t.r * left), 0, TAU);
    c.stroke();

    // Four ticks on the rim, so a telegraph is never mistaken for a
    // bullet's glow at low contrast.
    c.globalAlpha = 0.85 * pulse;
    c.lineWidth = Math.max(4, 2 * px);
    c.beginPath();
    for (let q = 0; q < 4; q++) {
      const turn = q / 4;
      const cx = cosT(turn);
      const cy = sinT(turn);
      c.moveTo(t.x + cx * (t.r - 10), t.y + cy * (t.r - 10));
      c.lineTo(t.x + cx * (t.r + 16), t.y + cy * (t.r + 16));
    }
    c.stroke();
  }

  c.globalAlpha = 1;
}

function drawZombies(
  c: CanvasRenderingContext2D,
  state: SimState,
  px: number,
  now: number,
  reduced: boolean,
): void {
  if (state.zombies.length === 0) return;

  for (let i = 0; i < state.zombies.length; i++) {
    const z = state.zombies[i]!;

    c.strokeStyle = DAEMON_PALETTE.zombie;
    c.globalAlpha = 0.62;
    c.lineWidth = Math.max(3.5, 1.5 * px);
    c.setLineDash(DASH_ZOMBIE);
    c.lineDashOffset = reduced ? 0 : -((now * 0.018) % 18);
    c.beginPath();
    c.arc(z.x, z.y, ZOMBIE_R, 0, TAU);
    c.stroke();
    c.setLineDash(DASH_NONE);

    // A dead process, marked as one.
    const g = ZOMBIE_R * 0.42;
    c.globalAlpha = 0.75;
    c.beginPath();
    c.moveTo(z.x - g, z.y - g);
    c.lineTo(z.x + g, z.y + g);
    c.moveTo(z.x + g, z.y - g);
    c.lineTo(z.x - g, z.y + g);
    c.stroke();

    // Reap progress. Dwelling is invisible without it, and an invisible
    // progress bar is a mechanic nobody finishes.
    const done = 1 - clamp(z.reap / REAP_REF, 0, 1);
    if (done > 0.001) {
      c.strokeStyle = DAEMON_PALETTE.reap;
      c.globalAlpha = 0.95;
      c.lineWidth = Math.max(5, 2 * px);
      c.beginPath();
      c.arc(z.x, z.y, ZOMBIE_R + 9, -0.25 * TAU, (-0.25 + done) * TAU);
      c.stroke();
    }
  }

  c.globalAlpha = 1;
}

function drawChildren(
  c: CanvasRenderingContext2D,
  state: SimState,
  px: number,
  now: number,
  reduced: boolean,
): void {
  if (state.children.length === 0) return;
  const spin = reduced ? 0 : (now * 0.00005) % 1;

  for (let i = 0; i < state.children.length; i++) {
    const ch = state.children[i]!;

    c.beginPath();
    for (let v = 0; v < 6; v++) {
      const turn = spin + v / 6;
      const x = ch.x + cosT(turn) * CHILD_R;
      const y = ch.y + sinT(turn) * CHILD_R;
      if (v === 0) c.moveTo(x, y);
      else c.lineTo(x, y);
    }
    c.closePath();

    c.fillStyle = DAEMON_PALETTE.child;
    c.globalAlpha = 0.16;
    c.fill();
    c.strokeStyle = DAEMON_PALETTE.child;
    c.globalAlpha = 0.92;
    c.lineWidth = Math.max(4, 1.5 * px);
    c.stroke();

    const hp = ch.hpMax > 0 ? clamp(ch.hp / ch.hpMax, 0, 1) : 0;
    c.globalAlpha = 0.2;
    c.lineWidth = Math.max(5, 2 * px);
    c.beginPath();
    c.arc(ch.x, ch.y, CHILD_R + 11, 0, TAU);
    c.stroke();
    if (hp > 0) {
      c.globalAlpha = 0.95;
      c.beginPath();
      c.arc(ch.x, ch.y, CHILD_R + 11, -0.25 * TAU, (-0.25 + hp) * TAU);
      c.stroke();
    }
  }

  c.globalAlpha = 1;
}

function drawDaemon(
  c: CanvasRenderingContext2D,
  state: SimState,
  prev: SimState,
  a: number,
  px: number,
  now: number,
  reduced: boolean,
  halo: CanvasGradient,
): void {
  const d = state.daemon;
  const p = prev.daemon;
  const x = p.x + (d.x - p.x) * a;
  const y = p.y + (d.y - p.y) * a;
  const hp = d.hpMax > 0 ? clamp(d.hp / d.hpMax, 0, 1) : 0;

  c.save();
  c.translate(x, y);

  c.globalAlpha = 1;
  c.fillStyle = halo;
  c.beginPath();
  c.arc(0, 0, DAEMON_R * 2.4, 0, TAU);
  c.fill();

  const spin = reduced ? 0 : (now * 0.00006) % 1;

  // Body: a dark disc so bullets leaving it are legible against it.
  c.fillStyle = DAEMON_PALETTE.floor;
  c.globalAlpha = 0.94;
  c.beginPath();
  c.arc(0, 0, DAEMON_R, 0, TAU);
  c.fill();

  c.strokeStyle = DAEMON_PALETTE.daemon;
  c.globalAlpha = 0.95;
  c.lineWidth = Math.max(5, 2 * px);
  c.beginPath();
  c.arc(0, 0, DAEMON_R, 0, TAU);
  c.stroke();

  // Two counter-rotating triangles. Reads as a process doing something
  // rather than as a sprite sitting there.
  c.globalAlpha = 0.8;
  c.lineWidth = Math.max(4, 1.5 * px);
  for (let s = 0; s < 2; s++) {
    const dir = s === 0 ? 1 : -1;
    const r = s === 0 ? DAEMON_R * 0.62 : DAEMON_R * 0.4;
    c.beginPath();
    for (let v = 0; v < 3; v++) {
      const turn = spin * dir + v / 3 + (s === 0 ? 0 : 1 / 6);
      const vx = cosT(turn) * r;
      const vy = sinT(turn) * r;
      if (v === 0) c.moveTo(vx, vy);
      else c.lineTo(vx, vy);
    }
    c.closePath();
    c.stroke();
  }

  // Handler window. SIGTERM bounces while this is up, so it has to be
  // visible from wherever the cursor happens to be.
  if (d.guarded) {
    c.strokeStyle = DAEMON_PALETTE.guard;
    c.globalAlpha = 0.9;
    c.lineWidth = Math.max(6, 2 * px);
    c.setLineDash(DASH_GUARD);
    c.lineDashOffset = reduced ? 0 : -((now * 0.03) % 24);
    c.beginPath();
    c.arc(0, 0, DAEMON_R + 16, 0, TAU);
    c.stroke();
    c.setLineDash(DASH_NONE);
  }

  // HP as a ring on the body. The HUD owns the authoritative bar; this
  // is so the number you are hitting is where you are looking.
  c.strokeStyle = DAEMON_PALETTE.daemon;
  c.globalAlpha = 0.22;
  c.lineWidth = Math.max(7, 2.5 * px);
  c.beginPath();
  c.arc(0, 0, DAEMON_R + 30, 0, TAU);
  c.stroke();
  if (hp > 0) {
    c.globalAlpha = 1;
    c.beginPath();
    c.arc(0, 0, DAEMON_R + 30, -0.25 * TAU, (-0.25 + hp) * TAU);
    c.stroke();
  }

  c.restore();
  c.globalAlpha = 1;
}

/**
 * Bullets.
 *
 * Two passes. The first walks the current and previous bullet lists
 * together — both are sorted ascending by id, because ids are allocated
 * monotonically and every sim stage preserves order — and writes
 * interpolated positions into a reusable buffer. A bullet with no match
 * in the previous list was born this tick and is drawn where it is.
 *
 * The second pass draws one kind at a time so the sprite bound as the
 * `drawImage` source changes six times a frame rather than three hundred.
 *
 * Returns the scratch buffer, which may have been grown — the caller
 * keeps it for the next frame so steady state allocates nothing.
 */
function drawBullets(
  c: CanvasRenderingContext2D,
  state: SimState,
  prev: SimState,
  a: number,
  sprites: (HTMLCanvasElement | null)[],
  buf: Float32Array,
): Float32Array {
  const cur = state.bullets;
  const n = cur.length;
  if (n === 0) return buf;

  const out = buf.length < n * 4 ? new Float32Array(Math.max(n * 4, buf.length * 2)) : buf;

  const old = prev.bullets;
  let j = 0;
  for (let i = 0; i < n; i++) {
    const b = cur[i]!;
    let x = b.x;
    let y = b.y;
    while (j < old.length && old[j]!.id < b.id) j++;
    if (j < old.length && old[j]!.id === b.id) {
      const o = old[j]!;
      x = o.x + (b.x - o.x) * a;
      y = o.y + (b.y - o.y) * a;
    }
    const at = i * 4;
    out[at] = x;
    out[at + 1] = y;
    out[at + 2] = b.r;
    out[at + 3] = KIND_INDEX[b.kind] ?? 0;
  }

  c.globalAlpha = 1;
  for (let ki = 0; ki < sprites.length; ki++) {
    const sprite = sprites[ki];
    if (!sprite) continue;
    for (let i = 0; i < n; i++) {
      const at = i * 4;
      if (out[at + 3] !== ki) continue;
      const r = out[at + 2] * GLOW_PAD;
      c.drawImage(sprite, out[at] - r, out[at + 1] - r, r * 2, r * 2);
    }
  }

  return out;
}

/**
 * The player.
 *
 * The bright dot is `PLAYER_R` exactly — the same radius the sim tests
 * against. That is not a style choice: in a game where a single pixel
 * of overlap ends a four-minute run, the player is entitled to see the
 * thing being measured. Everything larger (the cursor ring, the graze
 * ring) is drawn dimmer and thinner so it can never be mistaken for the
 * hitbox.
 *
 * The graze ring is drawn at `PLAYER_R + GRAZE_R` because grazing is
 * the only source of PRIV, and a resource you earn by an invisible
 * proximity rule is a resource nobody learns to farm.
 */
function drawPlayer(
  c: CanvasRenderingContext2D,
  state: SimState,
  prev: SimState,
  a: number,
  px: number,
  now: number,
  reduced: boolean,
  glow: CanvasGradient,
): void {
  const p = state.player;
  const q = prev.player;
  const x = q.x + (p.x - q.x) * a;
  const y = q.y + (p.y - q.y) * a;
  const hit = p.iframes > 0;

  c.strokeStyle = DAEMON_PALETTE.graze;
  c.globalAlpha = 0.32;
  c.lineWidth = Math.max(2, px);
  c.setLineDash(DASH_GRAZE);
  c.lineDashOffset = reduced ? 0 : -((now * 0.012) % 16);
  c.beginPath();
  c.arc(x, y, PLAYER_R + GRAZE_R, 0, TAU);
  c.stroke();
  c.setLineDash(DASH_NONE);

  c.strokeStyle = hit ? DAEMON_PALETTE.iframe : DAEMON_PALETTE.playerRim;
  c.globalAlpha = hit ? 0.95 : 0.6;
  c.lineWidth = Math.max(2.5, 1.2 * px);
  c.beginPath();
  c.arc(x, y, CURSOR_R, 0, TAU);
  c.stroke();

  // PRIV on the cursor, where the eye already is. The HUD carries the
  // real meter; this is peripheral confirmation that a graze paid out.
  const priv = p.privMax > 0 ? clamp(p.priv / p.privMax, 0, 1) : 0;
  if (priv > 0.001) {
    c.strokeStyle = DAEMON_PALETTE.priv;
    c.globalAlpha = 0.9;
    c.lineWidth = Math.max(3, 1.2 * px);
    c.beginPath();
    c.arc(x, y, CURSOR_R + 6, -0.25 * TAU, (-0.25 + priv) * TAU);
    c.stroke();
  }

  c.save();
  c.translate(x, y);
  c.globalAlpha = 1;
  c.fillStyle = glow;
  c.beginPath();
  c.arc(0, 0, PLAYER_R * 4, 0, TAU);
  c.fill();
  c.restore();

  // The hitbox. Blinks during i-frames, but never below half opacity —
  // a hitbox you cannot see is worse than one you cannot use, and
  // reduced motion holds it solid.
  c.globalAlpha = hit && !reduced ? 0.55 + 0.45 * (0.5 + 0.5 * sinT(now * 0.006)) : 1;
  c.fillStyle = DAEMON_PALETTE.player;
  c.beginPath();
  c.arc(x, y, PLAYER_R, 0, TAU);
  c.fill();
  c.strokeStyle = DAEMON_PALETTE.playerRim;
  c.lineWidth = Math.max(1.5, px);
  c.beginPath();
  c.arc(x, y, PLAYER_R, 0, TAU);
  c.stroke();

  c.globalAlpha = 1;
}
